import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  FormGroup,
} from "reactstrap";
import { saveCashLedger } from "../../components/Api/GmsCashLedger";
import { getBanks } from "../../components/Api/GmsBank";
import { getBankAccounts } from "../../components/Api/GmsBankAccount";
import { getCostHeads } from "../../components/Api/GmsCostHead";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getSuppliers } from "../../components/Api/GmsSupplier";
import { getOtherPayees } from "../../components/Api/GmsOtherPayee";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI } from "../../helpers/dateHelper";

// Custom styles for Select components (from PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
    overflowY: 'auto',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
  };
};

const defaultForm = {
  Date: new Date(),
  Amount: 0,
  TransactorType: null,
  TransactorId: null,
  CostHeadId: null,
  Notes: "",
};

export default function PettyCashPage() {
  const location = useLocation();
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  
  // Determine mode from URL path
  const isReceipt = location.pathname.includes('Receipts') && !location.pathname.includes('List');
  const pageTitle = isReceipt ? "Petty Cash Receipts" : "Petty Cash Payments";
  const breadcrumbTitle = isReceipt ? "Petty Cash Receipts" : "Petty Cash Payments";
  
  // Transactor type options (Cost Head removed - it's now a separate field)
  const transactorTypeOptions = [
    { value: 'ba', label: 'Banks' },
    { value: 'em', label: 'Employee' },
    { value: 'su', label: 'Supplier' },
    { value: 'ot', label: 'Others' },
  ];
  
  // State for transactor data
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [costHeads, setCostHeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [otherPayees, setOtherPayees] = useState([]);
  const [transactorOptions, setTransactorOptions] = useState([]);
  const [loadingTransactors, setLoadingTransactors] = useState(false);
  
  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const pagePath = isReceipt ? '/GmsPettyCashReceipts' : '/GmsPettyCashPayments';
  const hasPermission = hasPagePermissionWithAdmin(permissions, pagePath);
  const isViewOnly = hasViewOnlyPermission(permissions, pagePath);
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, pagePath);
  
  // Get user session
  const session = useMemo(() => getUserSession(), []);
  
  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);
  
  useEffect(() => {
    if (form.TransactorType) {
      loadTransactors(form.TransactorType);
    } else {
      setTransactorOptions([]);
      setForm(prev => ({ ...prev, TransactorId: null }));
    }
  }, [form.TransactorType]);
  
  useEffect(() => {
    if (form.TransactorType === 'ba' && selectedBank) {
      loadBankAccounts(selectedBank.value);
    } else if (form.TransactorType === 'ba') {
      setBankAccounts([]);
    }
  }, [selectedBank, form.TransactorType]);
  
  const loadInitialData = async () => {
    try {
      setLoading(true);
      // Load all transactor types data
      const [banksResponse, costHeadsData, employeesData, suppliersData, otherPayeesData] = await Promise.all([
        getBanks({}),
        getCostHeads({}),
        getEmployees({}),
        getSuppliers({}),
        getOtherPayees({}),
      ]);
      
      // Extract banks array from response object (response has {banks: [...], totalCount, page, pageSize})
      const banksArray = Array.isArray(banksResponse?.banks) ? banksResponse.banks : (Array.isArray(banksResponse) ? banksResponse : []);
      setBanks(banksArray);
      
      // Other APIs return arrays directly, but add defensive checks
      setCostHeads(Array.isArray(costHeadsData) ? costHeadsData : []);
      setEmployees(Array.isArray(employeesData) ? employeesData : []);
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      setOtherPayees(Array.isArray(otherPayeesData) ? otherPayeesData : []);
    } catch (error) {
      console.error("Error loading initial data:", error);
      Swal.fire("Error", "Failed to load initial data", "error");
    } finally {
      setLoading(false);
    }
  };
  
  const loadTransactors = async (transactorType) => {
    try {
      setLoadingTransactors(true);
      setTransactorOptions([]);
      setForm(prev => ({ ...prev, TransactorId: null }));
      
      switch (transactorType) {
        case 'ba':
          // For banks, show bank list first
          // Handle both array and ensure it's an array
          const banksArray = Array.isArray(banks) ? banks : [];
          const bankOptions = banksArray.map(b => ({
            value: b.bankId || b.BankId || b.bank_Id || b.Bank_Id,
            label: b.bankNameEn || b.BankNameEn || b.bank_NameEn || b.Bank_NameEn || b.bank_Name || b.Bank_Name || '',
          }));
          setTransactorOptions(bankOptions);
          break;
        case 'em':
          // Handle both array and ensure it's an array
          const employeesArray = Array.isArray(employees) ? employees : [];
          const employeeOptions = employeesArray.map(emp => ({
            value: emp.employee_Id || emp.Employee_Id || emp.employeeId || emp.EmployeeId,
            label: `${emp.employee_Code || emp.Employee_Code || emp.employeeCode || emp.EmployeeCode || ''} - ${emp.employee_NameEn || emp.Employee_NameEn || emp.employeeNameEn || emp.EmployeeNameEn || ''}`.trim(),
          }));
          setTransactorOptions(employeeOptions);
          break;
        case 'su':
          // Handle both array and ensure it's an array
          const suppliersArray = Array.isArray(suppliers) ? suppliers : [];
          const supplierOptions = suppliersArray.map(sup => ({
            value: sup.supplier_Id || sup.Supplier_Id || sup.supplierId || sup.SupplierId,
            label: sup.supplier_NameEn || sup.Supplier_NameEn || sup.supplierNameEn || sup.SupplierNameEn || sup.supplier_Name || sup.Supplier_Name || '',
          }));
          setTransactorOptions(supplierOptions);
          break;
        case 'ot':
          // Handle both array and ensure it's an array
          const otherPayeesArray = Array.isArray(otherPayees) ? otherPayees : [];
          const otherPayeeOptions = otherPayeesArray.map(op => ({
            value: op.otherPayee_Id || op.OtherPayee_Id || op.otherPayeeId || op.OtherPayeeId,
            label: op.otherPayee_Name || op.OtherPayee_Name || op.otherPayeeName || op.OtherPayeeName || '',
          }));
          setTransactorOptions(otherPayeeOptions);
          break;
        default:
          setTransactorOptions([]);
      }
    } catch (error) {
      console.error("Error loading transactors:", error);
      Swal.fire("Error", "Failed to load transactor data", "error");
    } finally {
      setLoadingTransactors(false);
    }
  };
  
  const loadBankAccounts = async (bankId) => {
    try {
      setLoadingTransactors(true);
      const accountsResponse = await getBankAccounts({ bankId: bankId });
      // Extract bankAccounts array from response object (response has {bankAccounts: [...], totalCount, page, pageSize})
      const accountsArray = accountsResponse?.bankAccounts || accountsResponse || [];
      const accountOptions = accountsArray.map(acc => {
        const accountHolderName = acc.accountHolderName || acc.AccountHolderName || '';
        const accountNumber = acc.accountNumber || acc.AccountNumber || '';
        const label = accountHolderName && accountNumber 
          ? `${accountHolderName} - ${accountNumber}`
          : accountHolderName || accountNumber || acc.bankAccount_Name || acc.BankAccount_Name || '';
        
        return {
          value: acc.bankAccountId || acc.BankAccountId || acc.bankAccount_Id || acc.BankAccount_Id,
          label: label,
        };
      });
      setBankAccounts(accountOptions);
      setTransactorOptions(accountOptions);
    } catch (error) {
      console.error("Error loading bank accounts:", error);
      Swal.fire("Error", "Failed to load bank accounts", "error");
    } finally {
      setLoadingTransactors(false);
    }
  };
  
  const handleFormChange = (field, value) => {
    if (field === 'TransactorType') {
      setForm(prev => ({ 
        ...prev, 
        [field]: value,
        TransactorId: null 
      }));
      setSelectedBank(null);
    } else if (field === 'TransactorId') {
      setForm(prev => ({ ...prev, [field]: value }));
    } else {
      setForm(prev => ({ ...prev, [field]: value }));
    }
  };
  
  const handleBankChange = (option) => {
    setSelectedBank(option);
    if (option) {
      loadBankAccounts(option.value);
    } else {
      setBankAccounts([]);
      setTransactorOptions([]);
      setForm(prev => ({ ...prev, TransactorId: null }));
    }
  };
  
  const handleSave = async () => {
    try {
      // Validation
      if (!form.Date) {
        Swal.fire("Error", "Please select a date", "error");
        return;
      }
      if (!form.Amount || form.Amount <= 0) {
        Swal.fire("Error", "Please enter a valid amount", "error");
        return;
      }
      if (!form.TransactorType) {
        Swal.fire("Error", "Please select a transactor type", "error");
        return;
      }
      if (!form.TransactorId) {
        Swal.fire("Error", "Please select a transactor", "error");
        return;
      }
      
      setSaveLoading(true);
      
      const cashLedgerData = {
        SlNo: 0, // Will be auto-calculated in stored procedure
        Date: formatDateForAPI(form.Date, true),
        Debit: isReceipt ? form.Amount : null,
        Credit: !isReceipt ? form.Amount : null,
        Notes: form.Notes || null,
        TransactorType: form.TransactorType,
        TransactorId: form.TransactorId,
        CostHeadId: form.CostHeadId || null,
        CreatedBy: session.userId,
      };
      
      const result = await saveCashLedger(cashLedgerData);
      
      if (result.isSuccess) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: result.message || 'Cash ledger entry saved successfully',
        });
        
        // Reset form
        setForm({
          ...defaultForm,
          Date: new Date(),
        });
        setSelectedBank(null);
        setBankAccounts([]);
        setTransactorOptions([]);
      } else {
        Swal.fire("Error", result.message || "Failed to save cash ledger entry", "error");
      }
    } catch (error) {
      console.error("Error saving cash ledger:", error);
      Swal.fire("Error", "An error occurred while saving the cash ledger entry", "error");
    } finally {
      setSaveLoading(false);
    }
  };
  
  if (!hasPermission) {
    return null;
  }
  
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title={breadcrumbTitle} breadcrumbItem={breadcrumbTitle} />
          
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  <h4 className="card-title mb-4">{pageTitle}</h4>
                  
                  {isViewOnly && (
                    <div className="alert alert-info">
                      <i className="mdi mdi-information me-2"></i>
                      You have view-only access. You cannot make changes.
                    </div>
                  )}
                  
                  {hasEditOrHigher && (
                    <Form>
                      <Row>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Date <span className="text-danger">*</span></Label>
                            <DatePicker
                              selected={form.Date}
                              onChange={(date) => handleFormChange("Date", date)}
                              dateFormat="dd/MM/yyyy"
                              className="form-control"
                              maxDate={new Date()}
                              disabled={isViewOnly}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Amount <span className="text-danger">*</span></Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={form.Amount}
                              onChange={(e) => handleFormChange("Amount", parseFloat(e.target.value) || 0)}
                              disabled={isViewOnly}
                              placeholder="Enter amount"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Transactor Type <span className="text-danger">*</span></Label>
                            <Select
                              value={transactorTypeOptions.find(opt => opt.value === form.TransactorType) || null}
                              onChange={(option) => handleFormChange("TransactorType", option?.value || null)}
                              options={transactorTypeOptions}
                              isClearable
                              isDisabled={isViewOnly}
                              placeholder="Select transactor type"
                              styles={selectStyles}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Transactor Selection <span className="text-danger">*</span></Label>
                            {form.TransactorType === 'ba' && !selectedBank ? (
                              <Select
                                value={transactorOptions.find(opt => opt.value === form.TransactorId) || null}
                                onChange={handleBankChange}
                                options={transactorOptions}
                                isClearable
                                isDisabled={isViewOnly || loadingTransactors}
                                placeholder="Select bank"
                                isLoading={loadingTransactors}
                                styles={selectStyles}
                              />
                            ) : (
                              <Select
                                value={transactorOptions.find(opt => opt.value === form.TransactorId) || null}
                                onChange={(option) => handleFormChange("TransactorId", option?.value || null)}
                                options={transactorOptions}
                                isClearable
                                isDisabled={isViewOnly || loadingTransactors || (form.TransactorType === 'ba' && !selectedBank)}
                                placeholder={
                                  form.TransactorType === 'ba' && !selectedBank 
                                    ? "Select bank first" 
                                    : form.TransactorType === 'ba' && selectedBank
                                    ? "Select bank account"
                                    : "Select transactor"
                                }
                                isLoading={loadingTransactors}
                                styles={selectStyles}
                              />
                            )}
                          </FormGroup>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={6}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Cost Head</Label>
                            <Select
                              value={costHeads.find(ch => (ch.costHead_Id || ch.CostHead_Id) === form.CostHeadId) 
                                ? { 
                                    value: form.CostHeadId, 
                                    label: costHeads.find(ch => (ch.costHead_Id || ch.CostHead_Id) === form.CostHeadId)?.costHead_Name || 
                                           costHeads.find(ch => (ch.costHead_Id || ch.CostHead_Id) === form.CostHeadId)?.CostHead_Name || '' 
                                  }
                                : null}
                              onChange={(option) => handleFormChange("CostHeadId", option?.value || null)}
                              options={costHeads.map(ch => ({
                                value: ch.costHead_Id || ch.CostHead_Id,
                                label: ch.costHead_Name || ch.CostHead_Name || '',
                              }))}
                              isClearable
                              isDisabled={isViewOnly}
                              placeholder="Select cost head"
                              styles={selectStyles}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={12}>
                          <FormGroup className="mb-3">
                            <Label className="mb-2">Notes</Label>
                            <Input
                              type="textarea"
                              rows="3"
                              value={form.Notes}
                              onChange={(e) => handleFormChange("Notes", e.target.value)}
                              disabled={isViewOnly}
                              placeholder="Enter notes"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      
                      <Row>
                        <Col md={12}>
                          <div className="d-flex gap-2">
                            <Button
                              color="primary"
                              onClick={handleSave}
                              disabled={isViewOnly || saveLoading}
                            >
                              {saveLoading ? (
                                <>
                                  <ClipLoader size={15} color="#fff" className="me-2" />
                                  Saving...
                                </>
                              ) : (
                                "Save"
                              )}
                            </Button>
                            <Button
                              color="secondary"
                              onClick={() => {
                                setForm({
                                  ...defaultForm,
                                  Date: new Date(),
                                });
                                setSelectedBank(null);
                                setBankAccounts([]);
                                setTransactorOptions([]);
                              }}
                              disabled={isViewOnly || saveLoading}
                            >
                              Reset
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                  
                  {!hasEditOrHigher && (
                    <div className="alert alert-warning">
                      <i className="mdi mdi-alert me-2"></i>
                      You do not have permission to add or edit entries.
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

